export type FixtureScheduleInvalidation = {
  affectedGameweeks: number[]
  changedAt: string
  revision: string
}

type FixtureScheduleInvalidationBatcher = {
  dispose: () => void
  push: (invalidation: FixtureScheduleInvalidation) => void
}

const DECIMAL_REVISION = /^[1-9]\d*$/
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/
const BATCH_MS = 500

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function parseFixtureScheduleInvalidation(
  payload: unknown
): FixtureScheduleInvalidation | null {
  if (!isRecord(payload)) return null
  if (typeof payload.revision !== 'string' || !DECIMAL_REVISION.test(payload.revision)) {
    return null
  }
  if (
    typeof payload.changed_at !== 'string' ||
    !ISO_TIMESTAMP.test(payload.changed_at) ||
    !Number.isFinite(Date.parse(payload.changed_at))
  ) {
    return null
  }
  if (!Array.isArray(payload.affected_gameweeks) || payload.affected_gameweeks.length === 0) {
    return null
  }

  const affectedGameweeks = payload.affected_gameweeks
  if (
    !affectedGameweeks.every(
      (gameweek) =>
        typeof gameweek === 'number' &&
        Number.isInteger(gameweek) &&
        gameweek >= 1 &&
        gameweek <= 38
    )
  ) {
    return null
  }

  return {
    affectedGameweeks: [...new Set(affectedGameweeks as number[])].sort(
      (left, right) => left - right
    ),
    changedAt: payload.changed_at,
    revision: payload.revision
  }
}

export function isFixtureScheduleRevisionNewer(
  incomingRevision: string,
  currentRevision: string
): boolean {
  if (!DECIMAL_REVISION.test(incomingRevision) || !/^\d+$/.test(currentRevision)) return false
  try {
    return BigInt(incomingRevision) > BigInt(currentRevision)
  } catch {
    return false
  }
}

export function scheduleInvalidationAffectsGameweek(
  invalidation: FixtureScheduleInvalidation,
  gameweek: number
): boolean {
  return Number.isInteger(gameweek) && invalidation.affectedGameweeks.includes(gameweek)
}

export function createFixtureScheduleInvalidationBatcher(
  onBatch: (invalidation: FixtureScheduleInvalidation) => void,
  delayMs = BATCH_MS
): FixtureScheduleInvalidationBatcher {
  let timer: ReturnType<typeof setTimeout> | null = null
  let pending: FixtureScheduleInvalidation | null = null

  const flush = () => {
    timer = null
    const current = pending
    pending = null
    if (current) onBatch(current)
  }

  return {
    push(invalidation) {
      if (!pending) {
        pending = invalidation
      } else {
        const newest = isFixtureScheduleRevisionNewer(invalidation.revision, pending.revision)
          ? invalidation
          : pending
        pending = {
          affectedGameweeks: [
            ...new Set([...pending.affectedGameweeks, ...invalidation.affectedGameweeks])
          ].sort((left, right) => left - right),
          changedAt: newest.changedAt,
          revision: newest.revision
        }
      }
      if (timer === null) timer = setTimeout(flush, delayMs)
    },
    dispose() {
      if (timer !== null) clearTimeout(timer)
      timer = null
      pending = null
    }
  }
}
